import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { editorStore } from "../editor/store"
import { projectPolygonToLocalMetres, unprojectPolygonFromLocalMetres } from "../editor/projection"
import { SATELLITE_PROVIDERS, resolveSatelliteProvider, satelliteTileUrlTemplate } from "../editor/satellite_providers"

// "Показать на карте" modal. Flow:
//   1. Open → Leaflet boots with the chosen satellite source (via /tiles/ proxy).
//   2. Optional PKK overlay toggle (RU only by default).
//   3. Address search → geocoding proxy → clickable result list → map
//      recentres on clicked result.
//   4. User clicks map to drop polygon vertices. Undo/Clear available.
//   5. "Применить" converts to local metres and replaces the plot.
export default class extends Controller {
  static targets = ["modal", "mapContainer", "searchInput", "searchResults", "searchButton", "searchError", "vertexCount", "apply", "overlay"]

  connect() {
    this._markers = []
    this._vertices = []
    this._polyline = null
    this._polygon = null
    this._locale = document.documentElement.lang || "en"
    this._showOverlay = this._locale.startsWith("ru")
    this._onKeyDown = (e) => { if (e.key === "Escape") this.close() }
    this._rafHandle = null
  }

  disconnect() {
    this._destroyMap()
    document.body.classList.remove("map-modal-open")
    document.removeEventListener("keydown", this._onKeyDown)
    if (this._rafHandle) cancelAnimationFrame(this._rafHandle)
  }

  open() {
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.setAttribute("aria-hidden", "false")
    // Mobile dock + bottom sheet + contextual toolbar + onboarding hint all
    // fixed z-50 overlays that would otherwise occlude the modal footer on
    // small screens. CSS hides them while the modal is open.
    document.body.classList.add("map-modal-open")
    document.addEventListener("keydown", this._onKeyDown)
    // Defer map init so the container has a real size.
    this._rafHandle = requestAnimationFrame(() => {
      this._rafHandle = null
      this._ensureMap()
      this._renderCurrentPlot()
    })
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.modalTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove("map-modal-open")
    document.removeEventListener("keydown", this._onKeyDown)
    if (this._rafHandle) { cancelAnimationFrame(this._rafHandle); this._rafHandle = null }
  }

  _ensureMap() {
    if (this._map) {
      this._map.invalidateSize()
      // The provider may have changed via the editor sidebar while the modal
      // was closed — re-activate the matching base layer so the two stay in sync.
      this._syncActiveBaseLayer()
      return
    }

    const centre = this._initialCentre()
    this._map = L.map(this.mapContainerTarget, {
      zoomControl: true,
      attributionControl: true,
    }).setView([centre.lat, centre.lng], centre.zoom)
    this._map.attributionControl.setPrefix('<a href="https://leafletjs.com" target="_blank" rel="noopener">Leaflet</a>')

    // Satellite tiles go through our /tiles/ proxy (keys stay server-side).
    // One base layer per provider with a Leaflet layers control so the user
    // can compare sources right where they draw the plot. Attribution is a
    // ToS requirement — must be visible whenever a source's tiles are.
    // maxNativeZoom caps real fetches at the source's native max; Leaflet
    // upscales the last sharp tile past that instead of requesting missing
    // tiles (mirrors the editor backdrop behaviour).
    this._baseLayers = {}
    this._layerToProvider = new Map()
    for (const cfg of Object.values(SATELLITE_PROVIDERS)) {
      const layer = L.tileLayer(satelliteTileUrlTemplate(cfg.id), {
        attribution: cfg.attribution
          .map((a) => (a.href ? `<a href="${a.href}" target="_blank" rel="noopener">${a.text}</a>` : a.text))
          .join(" "),
        maxZoom: 20,
        maxNativeZoom: cfg.maxZoom,
      })
      this._baseLayers[cfg.label] = layer
      this._layerToProvider.set(layer, cfg.id)
    }

    const activeCfg = resolveSatelliteProvider(editorStore.ui?.satelliteBg?.provider)
    this._satellite = this._baseLayers[activeCfg.label].addTo(this._map)
    L.control.layers(this._baseLayers, null, { collapsed: false }).addTo(this._map)
    // Keep the editor backdrop in sync with the source picked here.
    this._map.on("baselayerchange", (e) => {
      const id = this._layerToProvider.get(e.layer)
      if (id) editorStore.setUiSatelliteBgProvider(id)
    })

    this._pkkLayer = L.tileLayer("/tiles/pkk/{z}/{x}/{y}", {
      attribution: "&copy; Росреестр (ПКК)",
      opacity: 0.6,
      maxZoom: 19,
    })
    if (this._showOverlay) this._pkkLayer.addTo(this._map)
    if (this.hasOverlayTarget) this.overlayTarget.checked = this._showOverlay

    this._map.on("click", (e) => this._onMapClick(e))
  }

  // Switch the active Leaflet base layer to match the editor store's provider.
  // No-op when already in sync (so it does not fight the user mid-session).
  _syncActiveBaseLayer() {
    if (!this._map || !this._baseLayers) return
    const wantCfg = resolveSatelliteProvider(editorStore.ui?.satelliteBg?.provider)
    if (this._layerToProvider.get(this._satellite) === wantCfg.id) return
    const target = this._baseLayers[wantCfg.label]
    if (!target || target === this._satellite) return
    this._map.removeLayer(this._satellite)
    target.addTo(this._map)
    this._satellite = target
  }

  _renderCurrentPlot() {
    if (this._currentPlotLayer) {
      this._map.removeLayer(this._currentPlotLayer)
      this._currentPlotLayer = null
    }
    const plot = editorStore.plot
    if (!plot?.originLngLat) return

    const ring = unprojectPolygonFromLocalMetres(plot.polygonM, plot.originLngLat)
    const latlngs = ring.map(([lng, lat]) => [lat, lng])
    this._currentPlotLayer = L.polygon(latlngs, {
      color: "#4338ca", weight: 2, fillOpacity: 0.1, dashArray: "4 4",
    }).addTo(this._map)
    this._map.fitBounds(L.latLngBounds(latlngs).pad(0.3))
  }

  _initialCentre() {
    const plot = editorStore.plot
    if (plot?.originLngLat) {
      return { lat: plot.originLngLat[1], lng: plot.originLngLat[0], zoom: 18 }
    }
    return { lat: 55.751244, lng: 37.618423, zoom: 4 } // Moscow fallback; user will search
  }

  _onMapClick(e) {
    const latlng = [e.latlng.lat, e.latlng.lng]
    this._vertices.push(latlng)

    const marker = L.circleMarker(latlng, {
      radius: 5, color: "#4338ca", fillColor: "#ffffff", fillOpacity: 1, weight: 2,
    }).addTo(this._map)
    this._markers.push(marker)

    this._redrawOutline()
    this._updateControls()
  }

  _redrawOutline() {
    if (this._polyline) { this._map.removeLayer(this._polyline); this._polyline = null }
    if (this._polygon) { this._map.removeLayer(this._polygon); this._polygon = null }

    if (this._vertices.length === 2) {
      this._polyline = L.polyline(this._vertices, { color: "#4338ca", weight: 2 }).addTo(this._map)
    } else if (this._vertices.length >= 3) {
      this._polygon = L.polygon(this._vertices, {
        color: "#4338ca", weight: 2, fillColor: "#c7d2fe", fillOpacity: 0.35,
      }).addTo(this._map)
    }
  }

  _updateControls() {
    if (this.hasVertexCountTarget) this.vertexCountTarget.textContent = `${this._vertices.length}`
    if (this.hasApplyTarget) this.applyTarget.disabled = this._vertices.length < 3
  }

  undoVertex() {
    if (this._vertices.length === 0) return
    this._vertices.pop()
    const last = this._markers.pop()
    if (last) this._map.removeLayer(last)
    this._redrawOutline()
    this._updateControls()
  }

  clearVertices() {
    this._vertices = []
    this._markers.forEach((m) => this._map.removeLayer(m))
    this._markers = []
    this._redrawOutline()
    this._updateControls()
  }

  toggleOverlay(event) {
    this._showOverlay = !!event?.target?.checked
    if (!this._map || !this._pkkLayer) return
    if (this._showOverlay) this._pkkLayer.addTo(this._map)
    else this._map.removeLayer(this._pkkLayer)
  }

  // Lets the user retry via Enter without retyping — also guards against
  // blank submits with a clear message instead of silently doing nothing.
  async search(event) {
    event?.preventDefault?.()
    const q = this.searchInputTarget.value.trim()
    if (!q) {
      this._setSearchError("Введите адрес")
      return
    }
    this._setSearchError("")
    this._renderSearchResults([], "Ищу…")
    this._setSearching(true)

    try {
      const response = await fetch(`/geocoding?q=${encodeURIComponent(q)}`)
      const body = await response.json().catch(() => ({}))
      if (response.status === 429) {
        this._setSearchError(body.message || "Слишком много запросов")
        this._renderSearchResults([])
        return
      }
      if (!response.ok) {
        this._setSearchError(body.message || `Ошибка ${response.status}`)
        this._renderSearchResults([])
        return
      }
      const results = body.results || []
      if (results.length === 0) {
        this._renderSearchResults([], "Ничего не найдено")
        return
      }
      this._renderSearchResults(results)
      // Auto-centre on the first hit so a single-match query feels instant.
      const first = results[0]
      this._map.setView([first.lat, first.lon], 18)
    } catch (err) {
      this._setSearchError(`Сеть: ${err.message}`)
      this._renderSearchResults([])
    } finally {
      this._setSearching(false)
    }
  }

  pickResult(event) {
    const lat = parseFloat(event.currentTarget.dataset.lat)
    const lon = parseFloat(event.currentTarget.dataset.lon)
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      this._map.setView([lat, lon], 18)
    }
  }

  _renderSearchResults(results, emptyMessage = "") {
    if (!this.hasSearchResultsTarget) return
    this.searchResultsTarget.replaceChildren()
    if (results.length === 0) {
      if (emptyMessage) {
        const hint = document.createElement("div")
        hint.className = "text-xs text-neutral-500 px-1 py-1"
        hint.textContent = emptyMessage
        this.searchResultsTarget.appendChild(hint)
      }
      return
    }
    for (const r of results) {
      const row = document.createElement("button")
      row.type = "button"
      row.className = "block w-full text-left text-xs text-neutral-700 px-2 py-1 rounded hover:bg-indigo-50"
      row.dataset.lat = r.lat
      row.dataset.lon = r.lon
      row.dataset.action = "click->map#pickResult"
      row.textContent = r.display_name || "Без названия"
      this.searchResultsTarget.appendChild(row)
    }
  }

  _setSearchError(text) {
    if (!this.hasSearchErrorTarget) return
    this.searchErrorTarget.textContent = text
  }

  _setSearching(flag) {
    if (!this.hasSearchButtonTarget) return
    this.searchButtonTarget.disabled = flag
    this.searchButtonTarget.classList.toggle("opacity-60", flag)
  }

  apply() {
    if (this._vertices.length < 3) return
    // Leaflet gives [lat, lng]; our projection wants [lng, lat].
    const ring = [...this._vertices.map(([lat, lng]) => [lng, lat])]
    ring.push(ring[0])
    const { polygonM, originLngLat } = projectPolygonToLocalMetres(ring)
    editorStore.replacePlot({ source: "satellite", polygonM, originLngLat })
    this.clearVertices()
    this.close()
  }

  _destroyMap() {
    if (this._map) {
      this._map.remove()
      this._map = null
    }
  }
}
